import { useEffect, useState } from "react";
import { LogIn } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

export function LoginPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ enabled: boolean }>("/api/auth/sso/config")
      .then((data) => setSsoEnabled(!!data.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  useEffect(() => {
    const error = searchParams.get("sso_error");
    if (!error) return;
    const messages: Record<string, string> = {
      disabled: "SSO is not enabled for this environment.",
      failed: "SSO sign-in failed. Please try again.",
      inactive: "Your Demore account is not active.",
      not_invited: "Your SSO email is not invited to Demore yet.",
    };
    setSsoError(messages[error] ?? "SSO sign-in failed.");
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("sso_error");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/auth/login", { email, password });
      nav("/admin");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-admin-surface p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-6 mb-8">
          <Logo variant="wordmark" className="text-4xl" />
          <p className="text-sm text-muted-foreground">Sign in to Demore</p>
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-4 bg-admin-panel border border-admin-border rounded-md p-6"
        >
          {ssoError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {ssoError}
            </div>
          ) : null}
          {ssoEnabled ? (
            <>
              <Button
                asChild
                variant="outline"
                className="w-full border-gold/60 text-gold hover:bg-gold/10 hover:text-gold"
              >
                <a href="/api/auth/sso/start">
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign in with Elev8 SSO
                </a>
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-admin-border" />
                <span>or</span>
                <span className="h-px flex-1 bg-admin-border" />
              </div>
            </>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-center">
            <a href="/forgot" className="text-gold text-sm hover:underline">
              Forgot your password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
